import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { getAgentlessAvailability } from 'app/crud/agentless.crud';

// Server-decided agentless availability: hidden (flag off) / locked (unlicensed) / enabled.
// Cache is keyed by the current identity - impersonation must never reuse another user's answer.
const HIDDEN = { enabled: false, flagEnabled: false, licensed: false };
let cache = { key: null, value: null };
let inflight = { key: null, promise: null };

const fetchAvailability = (key) => {
  if (cache.key === key && cache.value) {
    return Promise.resolve(cache.value);
  }
  if (inflight.key !== key || !inflight.promise) {
    const promise = getAgentlessAvailability()
      .then((response) => {
        cache = {
          key,
          value: {
            enabled: response.data?.enabled === true,
            flagEnabled: response.data?.flag_enabled === true,
            licensed: response.data?.licensed === true,
          },
        };
        return cache.value;
      })
      .catch(() => HIDDEN) // not cached: a transient error may retry on next mount
      .finally(() => {
        if (inflight.promise === promise) {
          inflight = { key: null, promise: null };
        }
      });
    inflight = { key, promise };
  }
  return inflight.promise;
};

const useAgentlessAvailability = () => {
  const userKey = useSelector(
    ({ auth }) => `${auth.user?.id || ''}:${auth.impersonator?.id || ''}`
  );
  const [availability, setAvailability] = useState(
    cache.key === userKey && cache.value ? cache.value : HIDDEN
  );

  useEffect(() => {
    let mounted = true;
    fetchAvailability(userKey).then((value) => {
      if (mounted) {
        setAvailability(value);
      }
    });
    return () => {
      mounted = false;
    };
  }, [userKey]);

  return availability;
};

export default useAgentlessAvailability;
